import type { PowerInfo, PowerPrice } from './powers-data';

export const FAIRTRADE_URL = 'https://xatblog.net/fairtrade/';

/** Revalidação a cada 2 h (~12×/dia) — preços fairtrade mudam com frequência. */
export const FAIRTRADE_REVALIDATE_SECONDS = 7200;

export type FairtradePriceEntry = {
  slug: string;
  id: number;
  fairtrade_min: number;
  fairtrade_max: number;
  store: string;
};

export type FairtradePricesMap = Record<string, PowerPrice>;

export type FairtradePricesResponse = {
  code: number;
  count: number;
  updatedAt: string;
  source: string;
  prices: FairtradePricesMap;
};

const POWER_ROW_RE = /<tr class="power-[^"]*">[\s\S]*?<\/tr>/gi;
const TD_RE = /<td[^>]*>([\s\S]*?)<\/td>/gi;

function stripHtml(value: string): string {
  return value.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
}

function parsePriceCell(value: string): number {
  const n = Number.parseInt(stripHtml(value).replace(/[^\d]/g, ''), 10);
  return Number.isFinite(n) ? n : 0;
}

function parseStorePrice(value: string): string {
  const cleaned = stripHtml(value)
    .replace(/\s*xats?\s*$/i, '')
    .trim();
  return cleaned || 'unknown';
}

/** Extrai preços fairtrade e loja de cada linha da tabela xatblog FairTrade. */
export function parseFairtradePricesFromHtml(html: string): FairtradePriceEntry[] {
  const rows = html.match(POWER_ROW_RE) ?? [];

  return rows
    .map((row) => {
      const slug = row.match(/data-power="([^"]+)"/i)?.[1]?.trim().toLowerCase() ?? '';
      const cells = [...row.matchAll(TD_RE)].map((match) => match[1]);
      if (!slug || cells.length < 9) return null;

      const id = Number.parseInt(stripHtml(cells[0] ?? ''), 10);
      if (!Number.isFinite(id)) return null;

      return {
        slug,
        id,
        fairtrade_min: parsePriceCell(cells[4] ?? ''),
        fairtrade_max: parsePriceCell(cells[5] ?? ''),
        store: parseStorePrice(cells[8] ?? ''),
      };
    })
    .filter((entry): entry is FairtradePriceEntry => entry !== null);
}

export function fairtradeEntriesToMap(entries: FairtradePriceEntry[]): FairtradePricesMap {
  const map: FairtradePricesMap = {};
  for (const entry of entries) {
    map[entry.slug] = {
      fairtrade_min: entry.fairtrade_min,
      fairtrade_max: entry.fairtrade_max,
      store: entry.store,
    };
  }
  return map;
}

export async function fetchFairtradePricesFromSource(): Promise<FairtradePriceEntry[]> {
  const res = await fetch(FAIRTRADE_URL, {
    headers: { 'User-Agent': 'xat-ajuda/2.0' },
    cache: 'default',
  });

  if (!res.ok) {
    throw new Error(`FairTrade fetch failed (${res.status})`);
  }

  const html = await res.text();
  const entries = parseFairtradePricesFromHtml(html);

  if (entries.length === 0) {
    throw new Error('No fairtrade prices parsed from FairTrade page');
  }

  return entries;
}

export function hasFairtradePrice(prices: PowerPrice): boolean {
  return prices.fairtrade_min > 0 || prices.fairtrade_max > 0;
}

/** Prioriza preços da API/FairTrade; mock só como fallback. */
export function resolvePowerPrices(source: PowerPrice, fallback: PowerPrice): PowerPrice {
  const sourceHasFairtrade = hasFairtradePrice(source);
  const fallbackHasFairtrade = hasFairtradePrice(fallback);

  const fairtrade_min = sourceHasFairtrade
    ? source.fairtrade_min
    : fallbackHasFairtrade
      ? fallback.fairtrade_min
      : source.fairtrade_min;

  const fairtrade_max = sourceHasFairtrade
    ? source.fairtrade_max
    : fallbackHasFairtrade
      ? fallback.fairtrade_max
      : source.fairtrade_max;

  const store =
    source.store && source.store !== '0' && source.store !== 'unknown'
      ? source.store
      : fallback.store;

  return { fairtrade_min, fairtrade_max, store };
}

export function applyFairtradePrices(power: PowerInfo, map: FairtradePricesMap): PowerInfo {
  const entry = map[power.slug];
  if (!entry || !hasFairtradePrice(entry)) return power;

  return {
    ...power,
    prices: resolvePowerPrices(entry, power.prices),
  };
}

export function applyFairtradePricesToList(
  powers: PowerInfo[],
  map: FairtradePricesMap
): PowerInfo[] {
  if (Object.keys(map).length === 0) return powers;
  return powers.map((power) => applyFairtradePrices(power, map));
}

/** Normaliza respostas heterogéneas de fairtrade (Laravel/xat.com/xatblog). */
export function normalizeFairtradeApiResponse(data: unknown): FairtradePricesMap | null {
  if (!data || typeof data !== 'object') return null;

  const root = data as Record<string, unknown>;
  const prices = root.prices;
  if (!prices || typeof prices !== 'object') return null;

  const map: FairtradePricesMap = {};

  for (const [slug, value] of Object.entries(prices as Record<string, unknown>)) {
    const key = slug.trim().toLowerCase();
    if (!key) continue;

    if (value && typeof value === 'object') {
      const entry = value as Record<string, unknown>;
      const fairtrade_min = Number(entry.fairtrade_min ?? entry.min ?? 0) || 0;
      const fairtrade_max = Number(entry.fairtrade_max ?? entry.max ?? fairtrade_min) || 0;
      const storeRaw = entry.store ?? entry.c ?? entry.price;
      const store =
        typeof storeRaw === 'string' || typeof storeRaw === 'number'
          ? String(storeRaw)
          : 'unknown';

      map[key] = { fairtrade_min, fairtrade_max, store };
      continue;
    }

    if (typeof value === 'number') {
      map[key] = { fairtrade_min: value, fairtrade_max: value, store: 'unknown' };
      continue;
    }

    if (typeof value === 'string') {
      const amount = Number.parseInt(value.replace(/[^\d]/g, ''), 10) || 0;
      map[key] = { fairtrade_min: amount, fairtrade_max: amount, store: 'unknown' };
    }
  }

  return Object.keys(map).length > 0 ? map : null;
}
